import React, { useEffect, useState } from 'react';
import { Copy } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

const SAMPLES = {
  python: {
    label: 'Python',
    path: '/code/upload_universe_file.py',
    syntax: 'python',
  },
  shell: {
    label: 'Shell',
    path: '/code/upload_universe_file.sh',
    syntax: 'bash',
  },
};

/**
 * API page Code tab: sample scripts with language toggle and copy.
 * Scripts are served from public/code/ (mirrors scripts/upload_universe_file.*).
 */
function ApiCodeContent() {
  const [language, setLanguage] = useState('python');
  const [codeByLanguage, setCodeByLanguage] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const base = process.env.PUBLIC_URL || '';
    let cancelled = false;

    Promise.all(
      Object.entries(SAMPLES).map(async ([key, sample]) => {
        const res = await fetch(`${base}${sample.path}`);
        if (!res.ok) throw new Error(`Failed to load ${sample.label} sample`);
        const text = await res.text();
        return [key, text];
      })
    )
      .then((entries) => {
        if (cancelled) return;
        setCodeByLanguage(Object.fromEntries(entries));
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load code samples');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const code = codeByLanguage[language] || '';

  const copyCode = () => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    toast.success('Copied to clipboard');
  };

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">Upload a Universe</h2>

      <div className="flex items-center gap-6" role="radiogroup" aria-label="Language">
        {Object.entries(SAMPLES).map(([key, sample]) => (
          <label key={key} className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="codeLanguage"
              value={key}
              checked={language === key}
              onChange={() => setLanguage(key)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
            />
            <span className="text-sm font-medium text-gray-700">{sample.label}</span>
          </label>
        ))}
      </div>

      <div className="relative rounded-lg overflow-hidden min-h-[12rem] border border-gray-200">
        <button
          type="button"
          onClick={copyCode}
          disabled={loading || !code}
          className="absolute top-2 right-2 z-10 p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-40 disabled:pointer-events-none"
          aria-label="Copy code"
          title="Copy code"
        >
          <Copy className="h-4 w-4" />
        </button>
        {loading ? (
          <pre className="bg-white text-gray-700 rounded-lg p-4 pr-12 text-sm font-mono min-h-[12rem]">
            Loading…
          </pre>
        ) : (
          <SyntaxHighlighter
            language={SAMPLES[language].syntax}
            style={oneLight}
            customStyle={{
              margin: 0,
              borderRadius: '0.5rem',
              backgroundColor: '#ffffff',
              padding: '1rem 3rem 1rem 1rem',
              fontSize: '0.875rem',
              lineHeight: 1.625,
              minHeight: '12rem',
            }}
            codeTagProps={{
              style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' },
            }}
          >
            {code}
          </SyntaxHighlighter>
        )}
      </div>
    </div>
  );
}

export default ApiCodeContent;
